"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import Axios from "@/lib/axiosConfig";
import { useAuth } from "@/context/Auth";
import { ResponseSchema } from "../types";

interface UpdateEducationData {
  name: string;
  description: string;
  neighborhoodID: string;
  overview: string;
  setting: string;
  enrollment: string;
  acceptanceRate: number;
  agesEnrolled: string;
  partOrFullTime: string;
  applicationDeadline?: string;
  applicationFee: number;
  studentFacultyRatio: string;
  method: string;
  topSecondaries: string[];
  thumbnailImage?: string;
  coverImage?: string;
  categoryID: string;
  curriculum: string;
  isReligious: boolean;
  specialties: string[];
  fullTimeCost?: string;
  fullTimeRegistrationCost: number;
  fullTimeSuppliesCost: number;
  partTimeCost?: string;
  partTimeRegistrationCost: number;
  partTimeSuppliesCost: number;
  doesProvideScholarships: boolean;
  doesProvideGrants: boolean;
  clubs: string[];
  sports: string[];
  neighborhoodCrimeInfo:string;
  email?:string;
  phone?:string;
  workHours?:string;
  website?:string;
  campusSize: number;
  culture: string;
  coordinates?: number[];
}

interface UpdateEducationResponse {
  id: string;
  userID: string;
  name: string;
  description: string;
  neighborhoodID: string;
  overview: string;
  setting: string;
  enrollment: string;
  acceptanceRate: number;
  agesEnrolled: string;
  partOrFullTime: string;
  applicationDeadline: string | null;
  applicationFee: number;
  studentFacultyRatio: string;
  method: string;
  topSecondaries: string[];
  thumbnailImage: string | null;
  coverImage: string | null;
  categoryID: string;
  curriculum: string;
  isReligious: boolean;
  specialties: string[];
  coordinates: [number, number] | null;
  fullTimeCost: string | null;
  fullTimeRegistrationCost: number;
  fullTimeSuppliesCost: number;
  partTimeCost: string | null;
  partTimeRegistrationCost: number;
  partTimeSuppliesCost: number;
  doesProvideScholarships: boolean;
  doesProvideGrants: boolean;
  clubs: string[];
  sports: string[];
  neighborhoodCrimeInfo:string;
  campusSize: number;
  email?:string;
  phone?:string;
  hours?:string;
  website?:string;
  culture: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  isReported: boolean;
  averageReviewScore: number | string | null;
  isReviewed: boolean;
  isOnWishlist: boolean;
}

export const useUpdateEducationMutation = (educationID: string) => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateEducationData) => {
      return Axios.put<ResponseSchema<UpdateEducationResponse>>(
        `/educations/${educationID}`,
        data,
        {
          headers: getAPIRequestTokenHeaders(),
        }
      ).then((res) => res.data);
    },
    onSuccess: () => {
      // Invalidate and refetch education data
      queryClient.invalidateQueries({ 
        queryKey: ["education", educationID] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ["educations"] 
      });
    },
  });
};
